import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../../componentes/input/input";
import InputFormat from "../../../../componentes/inputMask/inputMask";
import Button from "../../../../componentes/button/button";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import ClienteService from "../../../../services/resource/clienteService";
import SubCanalAtivoService from "../../../../services/resource/subCanalAtivoService";
import NovaSolicitacaoLayout from "../shared/NovaSolicitacaoLayout";
import { useRascunhoFormulario } from "../../Rascunho/RascunhoContext";
import { listaDoRascunho, mesclarComPadrao } from "../../../../utils/solicitacoes/rascunhoStorage";
import { enviarAnexosStaged } from "../../../../utils/solicitacoes/anexosStaged";
import { enviarComentariosStaged } from "../../../../utils/solicitacoes/comentariosStaged";

const clienteService = new ClienteService();
const subCanalService = new SubCanalAtivoService();

const CODIGO_CLIENTE_REGEX = /^\d{4}-\d{4}$/;

// Exigido só no frontend (web e mobile) — o backend não valida isso, mesmo padrão do Cadastro de Cliente.
const ANEXOS_OBRIGATORIOS_GTC = ["Foto do PDV Externo", "Foto do PDV Interno"];

const DIA_VISITA_OPTIONS = [
    { value: "SEGUNDA_FEIRA", label: "Segunda-feira" },
    { value: "TERCA_FEIRA", label: "Terça-feira" },
    { value: "QUARTA_FEIRA", label: "Quarta-feira" },
    { value: "QUINTA_FEIRA", label: "Quinta-feira" },
    { value: "SEXTA_FEIRA", label: "Sexta-feira" },
    { value: "SABADO", label: "Sábado" },
];

const inicial = {
    clienteCodigo: "",
    clienteEncontrado: null,
    razaoSocial: "",
    fantasia: "",
    incluiAlteracaoGtc: false,
    incluiAlteracaoRota: false,
    incluiAlteracaoVisita: false,
    incluiAtualizacaoCoordenadas: false,
    subCanalAtualId: null,
    subCanalPropostoId: null,
    rotaAtual: "",
    rotaProposta: "",
    diaVisitaAtual: null,
    diaVisitaProposto: null,
    coordenadas: "",
};

// "lat,lng" <-> dois campos separados — mantém `form.coordenadas` como fonte única (mesmo formato
// gravado pelo backend), só desmontando/remontando a string na hora de editar cada campo.
function partesCoordenadas(coordenadas) {
    const [lat, lng] = (coordenadas || "").split(",");
    return [lat?.trim() || "", lng?.trim() || ""];
}

// Mesma técnica condicional de NovaSolicitacaoPromocaoForm: o solicitante marca qualquer
// combinação dos 3 tipos de ajuste (ao menos um), e cada seção só aparece/exige seus campos
// quando marcada.
function CheckboxTipoAjuste({ label, checked, onChange }) {
    return (
        <label className="flex items-center gap-2.5 px-3 py-2 border border-input rounded-md text-sm font-medium text-foreground/80 cursor-pointer hover:bg-muted">
            <input
                type="checkbox"
                checked={checked}
                onChange={e => onChange(e.target.checked)}
                className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary/15"
            />
            {label}
        </label>
    );
}

// Busca automática (Geolocation do navegador) + edição manual de latitude/longitude — sem mapa
// interativo aqui (o pedido do usuário restringiu o mapa ao mobile Android; no web só GPS/manual).
function CampoCoordenadas({ coordenadas, onChange }) {
    const [buscando, setBuscando] = useState(false);
    const [lat, lng] = partesCoordenadas(coordenadas);

    function buscarLocalizacaoAtual() {
        if (!navigator.geolocation) {
            toast.error("Este navegador não suporta captura de localização.");
            return;
        }
        setBuscando(true);
        navigator.geolocation.getCurrentPosition(
            pos => {
                onChange(`${pos.coords.latitude},${pos.coords.longitude}`);
                setBuscando(false);
            },
            () => {
                toast.error("Não foi possível obter a localização — verifique a permissão do navegador.");
                setBuscando(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-l-2 border-primary/30 pl-4">
            <div className="sm:col-span-2">
                <button
                    type="button"
                    onClick={buscarLocalizacaoAtual}
                    disabled={buscando}
                    className="h-9 w-full sm:w-auto px-4 flex items-center justify-center gap-2 border border-input rounded-md text-sm text-foreground/80 hover:bg-muted transition-colors duration-150 disabled:opacity-60"
                >
                    <i className={`fa ${buscando ? "fa-spinner fa-spin" : "fa-location-arrow"}`} />
                    {buscando ? "Obtendo localização..." : "Buscar localização atual"}
                </button>
            </div>
            <Input type="number" label="Latitude" required value={lat} onChange={e => onChange(`${e.target.value},${lng}`)} placeholder="-5.083300" />
            <Input type="number" label="Longitude" required value={lng} onChange={e => onChange(`${lat},${e.target.value}`)} placeholder="-42.800000" />
        </div>
    );
}

export default function NovaSolicitacaoAjusteGtcForm({ service, etapaInicialExigeAnexo, onCriada, encaminhar }) {
    const [form, setForm] = useState(inicial);
    const [anexos, setAnexos] = useState([]);
    const [comentarios, setComentarios] = useState([]);

    // Rascunho (botão "Salvar rascunho" da página) — só campos, anexos ficam de fora.
    useRascunhoFormulario(
        () => ({ form, comentarios }),
        dados => {
            setForm(mesclarComPadrao(inicial, dados.form));
            setComentarios(listaDoRascunho(dados.comentarios));
        },
    );
    const [buscandoCliente, setBuscandoCliente] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [subCanais, setSubCanais] = useState([]);

    useEffect(() => {
        subCanalService.listarAtivos().then(res => setSubCanais(res.data)).catch(() => setSubCanais([]));
    }, []);

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    const anexosObrigatorios = form.incluiAlteracaoGtc ? ANEXOS_OBRIGATORIOS_GTC : [];
    const camposClienteDesabilitados = form.clienteEncontrado === true;

    function aoMudarCodigoCliente(valor) {
        const novoValor = valor || "";
        if (!CODIGO_CLIENTE_REGEX.test(novoValor)) {
            setForm(prev => ({ ...prev, clienteCodigo: novoValor, clienteEncontrado: null }));
            return;
        }
        setBuscandoCliente(true);
        clienteService.buscarPorCodigo(novoValor)
            .then(res => {
                const c = res.data;
                if (c.encontrado) {
                    setForm(prev => ({
                        ...prev, clienteCodigo: novoValor, clienteEncontrado: true,
                        razaoSocial: c.razaoSocial || "", fantasia: c.fantasia || "",
                    }));
                } else {
                    setForm(prev => ({ ...prev, clienteCodigo: novoValor, clienteEncontrado: false }));
                    toast.info("Cliente não encontrado. Preencha os dados manualmente.");
                }
            })
            .catch(() => toast.error("Erro ao buscar cliente."))
            .finally(() => setBuscandoCliente(false));
    }

    function validar() {
        if (!CODIGO_CLIENTE_REGEX.test(form.clienteCodigo)) {
            toast.error("Informe o código do cliente no formato 9999-9999.");
            return false;
        }
        if (!form.razaoSocial.trim() || !form.fantasia.trim()) {
            toast.error("Preencha a razão social e o nome fantasia.");
            return false;
        }
        if (!form.incluiAlteracaoGtc && !form.incluiAlteracaoRota && !form.incluiAlteracaoVisita && !form.incluiAtualizacaoCoordenadas) {
            toast.error("Selecione ao menos um tipo de solicitação: GTC, rota, visita ou coordenadas.");
            return false;
        }
        if (form.incluiAlteracaoGtc && (!form.subCanalAtualId || !form.subCanalPropostoId)) {
            toast.error("Selecione o subcanal atual e o subcanal proposto.");
            return false;
        }
        if (form.incluiAlteracaoRota && (!form.rotaAtual.trim() || !form.rotaProposta.trim())) {
            toast.error("Informe a rota atual e a rota proposta.");
            return false;
        }
        if (form.incluiAlteracaoVisita && (!form.diaVisitaAtual || !form.diaVisitaProposto)) {
            toast.error("Selecione o dia de visita atual e o dia de visita proposto.");
            return false;
        }
        if (form.incluiAtualizacaoCoordenadas) {
            const [lat, lng] = partesCoordenadas(form.coordenadas);
            if (!lat || !lng) {
                toast.error("Informe a latitude e a longitude, busque a localização atual ou use o mapa no app mobile.");
                return false;
            }
        }
        if (anexosObrigatorios.length > 0) {
            const faltando = anexosObrigatorios.filter(
                tipo => !anexos.some(item => item.descricao?.trim().toLowerCase() === tipo.toLowerCase())
            );
            if (faltando.length > 0) {
                toast.error(`Para alteração de GTC, é necessário anexar: ${faltando.join(", ")}.`);
                return false;
            }
        } else if (etapaInicialExigeAnexo && anexos.length === 0) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return false;
        }
        return true;
    }

    async function enviar() {
        if (!validar()) return;

        let usuarioEscolhidoId;
        try {
            usuarioEscolhidoId = await encaminhar("ETAPA_2_ANALISE_SUPERVISOR");
        } catch {
            return;
        }

        setEnviando(true);
        service.criarSolicitacao({
            ...form,
            usuarioEscolhidoId,
            subCanalAtualId: form.incluiAlteracaoGtc ? form.subCanalAtualId : null,
            subCanalPropostoId: form.incluiAlteracaoGtc ? form.subCanalPropostoId : null,
            rotaAtual: form.incluiAlteracaoRota ? form.rotaAtual : null,
            rotaProposta: form.incluiAlteracaoRota ? form.rotaProposta : null,
            diaVisitaAtual: form.incluiAlteracaoVisita ? form.diaVisitaAtual : null,
            diaVisitaProposto: form.incluiAlteracaoVisita ? form.diaVisitaProposto : null,
            coordenadas: form.incluiAtualizacaoCoordenadas ? form.coordenadas : null,
        })
            .then(async res => {
                const [{ falha: falhaAnexos }, { falha: falhaComentarios }] = await Promise.all([
                    enviarAnexosStaged(res.data.id, anexos),
                    enviarComentariosStaged(res.data.id, comentarios),
                ]);
                if (falhaAnexos.length > 0 || falhaComentarios.length > 0) {
                    const partes = [];
                    if (falhaAnexos.length > 0) partes.push(`${falhaAnexos.length} anexo(s) (${falhaAnexos.map(f => `${f.arquivo.name}: ${f.erro}`).join(", ")})`);
                    if (falhaComentarios.length > 0) partes.push(`${falhaComentarios.length} comentário(s)`);
                    toast.error(`Solicitação criada, mas houve falha ao enviar: ${partes.join(" e ")}.`);
                }
                onCriada(res.data);
            })
            .catch(error => console.error("Erro ao criar solicitação de ajuste de GTC/rota/visita", error))
            .finally(() => setEnviando(false));
    }

    return (
        <NovaSolicitacaoLayout
            anexos={anexos}
            onAnexosChange={setAnexos}
            comentarios={comentarios}
            onComentariosChange={setComentarios}
            etapaInicialExigeAnexo={etapaInicialExigeAnexo}
            anexosObrigatorios={anexosObrigatorios}
        >
            <h3 className="text-sm font-semibold text-foreground">Cliente</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <InputFormat
                    label="Código do Cliente"
                    required
                    mask="9999-9999"
                    value={form.clienteCodigo}
                    onChange={aoMudarCodigoCliente}
                    placeholder="0000-0000"
                />
                <Input label="Razão Social" required disabled={camposClienteDesabilitados} value={form.razaoSocial} onChange={e => set("razaoSocial", e.target.value)} />
                <Input label="Nome Fantasia" required disabled={camposClienteDesabilitados} value={form.fantasia} onChange={e => set("fantasia", e.target.value)} />
            </div>
            {buscandoCliente && <p className="text-xs text-muted-foreground -mt-2">Buscando cliente...</p>}
            {form.clienteEncontrado === false && <p className="text-xs text-amber-600 -mt-2">Cliente não encontrado — preencha os dados manualmente.</p>}

            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-1.5">
                    Tipo de Solicitação <span className="text-destructive">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <CheckboxTipoAjuste label="Alteração de GTC" checked={form.incluiAlteracaoGtc} onChange={v => set("incluiAlteracaoGtc", v)} />
                    <CheckboxTipoAjuste label="Alteração de Rota" checked={form.incluiAlteracaoRota} onChange={v => set("incluiAlteracaoRota", v)} />
                    <CheckboxTipoAjuste label="Alteração de Visita" checked={form.incluiAlteracaoVisita} onChange={v => set("incluiAlteracaoVisita", v)} />
                    <CheckboxTipoAjuste label="Atualizar Coordenadas" checked={form.incluiAtualizacaoCoordenadas} onChange={v => set("incluiAtualizacaoCoordenadas", v)} />
                </div>
            </div>

            {form.incluiAlteracaoGtc && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-l-2 border-primary/30 pl-4">
                    <CustomDropdown label="Subcanal Atual" required value={form.subCanalAtualId} onChange={e => set("subCanalAtualId", e.value)}
                        options={subCanais} optionValue="id" optionLabel="nome" placeholder="Selecione" filter />
                    <CustomDropdown label="Subcanal Proposto" required value={form.subCanalPropostoId} onChange={e => set("subCanalPropostoId", e.value)}
                        options={subCanais} optionValue="id" optionLabel="nome" placeholder="Selecione" filter />
                </div>
            )}

            {form.incluiAlteracaoRota && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-l-2 border-primary/30 pl-4">
                    <Input label="Rota Atual" required value={form.rotaAtual} onChange={e => set("rotaAtual", e.target.value)} />
                    <Input label="Rota Proposta" required value={form.rotaProposta} onChange={e => set("rotaProposta", e.target.value)} />
                </div>
            )}

            {form.incluiAlteracaoVisita && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-l-2 border-primary/30 pl-4">
                    <CustomDropdown label="Dia de Visita Atual" required value={form.diaVisitaAtual} onChange={e => set("diaVisitaAtual", e.value)}
                        options={DIA_VISITA_OPTIONS} placeholder="Selecione" />
                    <CustomDropdown label="Dia de Visita Proposto" required value={form.diaVisitaProposto} onChange={e => set("diaVisitaProposto", e.value)}
                        options={DIA_VISITA_OPTIONS} placeholder="Selecione" />
                </div>
            )}

            {form.incluiAtualizacaoCoordenadas && (
                <CampoCoordenadas coordenadas={form.coordenadas} onChange={v => set("coordenadas", v)} />
            )}

            <div className="flex justify-end">
                <Button variant="blue" label="Solicitar ajuste" iconLeft="fa fa-paper-plane" loading={enviando} onClick={enviar} />
            </div>
        </NovaSolicitacaoLayout>
    );
}
